import React, { createContext, useCallback, useEffect, useState } from "react";
import { setCookie, getCookie, deleteCookie } from "../cookies/cookies.js";
import { CLIENT_ID, CLIENT_SECRET, NONCE, OAUTH, REDIRECT_URI, SCOPE, STATE } from "../Config.js";
import axios from "../api/axios";
import { clearAuthCookies, refreshAccessToken as requestRefreshAccessToken } from "../token/token.js";

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [token, setToken] = useState(() => getCookie("accessToken") || "");
    const [refreshToken, setRefreshToken] = useState(() => getCookie("refresh_token") || "");
    const [isAuthenticated, setAuthenticated] = useState(() => !!getCookie("accessToken"));
    const [tokenExpiresIn, setTokenExpiresIn] = useState(() => {
        const storedExpiresIn = getCookie("tokenExpiresIn");
        return storedExpiresIn ? parseInt(storedExpiresIn, 10) : null;
    });
    const [role, setRole] = useState(() => {
        const storedRoles = getCookie("roles");
        try {
            return storedRoles ? JSON.parse(storedRoles) : [];
        } catch (e) {
            return [];
        }
    });
    const [user, setUser] = useState(() => {
        const storedUser = getCookie("user");
        try {
            return storedUser ? JSON.parse(storedUser) : {};
        } catch (e) {
            return {};
        }
    });

    const saveToken = useCallback((newToken, expiresIn) => {
        setToken(newToken);
        if (newToken && expiresIn) {
            const expiryDate = new Date(expiresIn);
            setCookie("accessToken", newToken, expiryDate);
            setAuthenticated(true);
            return;
        }

        deleteCookie("accessToken");
        setAuthenticated(false);
    }, []);

    const saveRefreshToken = useCallback((newToken, expiresIn) => {
        setRefreshToken(newToken);
        if (newToken && expiresIn) {
            const expiryDate = new Date();
            expiryDate.setTime(expiryDate.getTime() + (expiresIn * 1000));
            setCookie("refresh_token", newToken, expiryDate);
            return;
        }

        deleteCookie("refresh_token");
    }, []);

    const saveAuthenticated = useCallback((data) => {
        setAuthenticated(data);
    }, []);

    const saveTokenExpiresIn = useCallback((newExpiresIn) => {
        setTokenExpiresIn(newExpiresIn);
        if (newExpiresIn) {
            const expiryDate = new Date(newExpiresIn);
            setCookie("tokenExpiresIn", newExpiresIn.toString(), expiryDate);
            return;
        }

        deleteCookie("tokenExpiresIn");
    }, []);

    const saveRole = useCallback((newRole, expiresIn) => {
        setRole(newRole);
        if (newRole && Array.isArray(newRole) && newRole.length > 0) {
            const expiryDate = new Date(expiresIn);
            setCookie("roles", JSON.stringify(newRole), expiryDate);
            return;
        }

        deleteCookie("roles");
    }, []);

    const saveUser = useCallback((newUser, expiresIn) => {
        setUser(newUser);
        if (newUser) {
            const expiryDate = new Date(expiresIn);
            setCookie("user", JSON.stringify(newUser), expiryDate);
            return;
        }

        deleteCookie("user");
    }, []);

    const doLogout = useCallback(() => {
        clearAuthCookies();
        setToken("");
        setTokenExpiresIn(null);
        setAuthenticated(false);
        setRefreshToken("");
        setRole([]);
        setUser(null);
    }, []);

    const syncSdkClientContext = useCallback(() => {
        if (!window.requestConnetxa) {
            return false;
        }

        window.requestConnetxa.setClientId(CLIENT_ID);
        window.requestConnetxa.setClientSecret(CLIENT_SECRET);
        if (typeof window.requestConnetxa.setRedirectUri === "function") {
            window.requestConnetxa.setRedirectUri(REDIRECT_URI);
        }

        return true;
    }, []);

    const waitForSdkReady = useCallback((timeoutMs = 3000, intervalMs = 50) => {
        return new Promise((resolve) => {
            if (window.requestConnetxa) {
                resolve(true);
                return;
            }

            const startedAt = Date.now();
            const timer = window.setInterval(() => {
                if (window.requestConnetxa) {
                    window.clearInterval(timer);
                    resolve(true);
                    return;
                }

                if (Date.now() - startedAt >= timeoutMs) {
                    window.clearInterval(timer);
                    resolve(false);
                }
            }, intervalMs);
        });
    }, []);

    const applyLoginSession = useCallback(async (tokenData) => {
        if (!tokenData || !tokenData.access_token) {
            return false;
        }

        const {
            access_token,
            expires_in,
            refresh_token,
            exp_timestamp,
            refresh_token_expires_in,
            user: tokenUser,
            roles: tokenRoles,
            userId
        } = tokenData;

        const finalExpiresIn = exp_timestamp ? exp_timestamp : Date.now() + ((expires_in || 0) * 1000);
        const expiryDate = new Date(finalExpiresIn);

        saveTokenExpiresIn(finalExpiresIn);
        saveToken(access_token, expiryDate);
        saveRefreshToken(refresh_token, refresh_token_expires_in);
        saveAuthenticated(true);

        try {
            if (window.requestConnetxa && typeof window.requestConnetxa.ssoSessionBootstrap === "function") {
                await window.requestConnetxa.ssoSessionBootstrap();
            } else {
                await axios.post(`${OAUTH}/sso/session/bootstrap`, {}, { withCredentials: true });
            }
        } catch (bootstrapError) {
            console.error("SSO session bootstrap failed during auto login:", bootstrapError);
        }

        let userInfo = tokenUser || null;
        let roles = Array.isArray(tokenRoles) ? tokenRoles.filter(Boolean) : null;

        try {
            const userRes = await axios.get(`${OAUTH}/userinfo`);
            userInfo = userRes.data;
            roles = Array.isArray(userRes.data?.roles) ? userRes.data.roles.filter(Boolean) : ["USER"];
        } catch (userInfoError) {
            console.error("Failed to load userinfo after auto login:", userInfoError);
            if (!roles || roles.length === 0) {
                roles = ["USER"];
            }
        }

        if (userInfo) {
            saveUser(userInfo, expiryDate);
            setCookie("user_id", userInfo.userId || userInfo.unifiedProfileId || userId || "", expiryDate);
        } else {
            saveUser(null);
            setCookie("user_id", userId || "", expiryDate);
        }

        saveRole(roles || ["USER"], expiryDate);
        return true;
    }, [saveAuthenticated, saveRefreshToken, saveRole, saveToken, saveTokenExpiresIn, saveUser]);

    const attemptCentralSessionAutoLogin = useCallback(async () => {
        const sdkReady = await waitForSdkReady();
        if (!sdkReady || !window.requestConnetxa || typeof window.requestConnetxa.ssoDirectEntryState !== "function") {
            return false;
        }

        syncSdkClientContext();

        try {
            const entryState = await window.requestConnetxa.ssoDirectEntryState({
                targetClientId: CLIENT_ID,
                targetRedirectUri: REDIRECT_URI
            });

            const autoLoginAvailable = entryState
                && entryState.directEntryStatus === window.requestConnetxa.DIRECT_ENTRY_STATUS.AUTO_LOGIN_AVAILABLE;

            if (!autoLoginAvailable) {
                return false;
            }

            const authorizeResult = await window.requestConnetxa.authorize(
                CLIENT_ID,
                REDIRECT_URI,
                SCOPE,
                STATE,
                NONCE,
                {
                    prompt: "none",
                    credentials: "include"
                }
            );

            const authorizationCode = authorizeResult?.code || authorizeResult?.data?.code || null;
            const authorizeError = authorizeResult?.error || authorizeResult?.code || authorizeResult?.data?.error || null;

            if (!authorizationCode) {
                if (authorizeError === "login_required" || authorizeError === "interaction_required") {
                    return false;
                }
                throw authorizeResult || new Error("Silent authorize failed.");
            }

            const tokenData = await window.requestConnetxa.token(
                authorizationCode,
                REDIRECT_URI,
                SCOPE,
                NONCE
            );

            return await applyLoginSession(tokenData);
        } catch (error) {
            console.error("Central session auto login failed:", error);
            return false;
        }
    }, [applyLoginSession, syncSdkClientContext, waitForSdkReady]);

    const refreshAccessToken = useCallback(async () => {
        const tokenData = await requestRefreshAccessToken();
        if (!tokenData) {
            return false;
        }

        return await applyLoginSession(tokenData);
    }, [applyLoginSession]);

    const initializeAuth = useCallback(async () => {
        setIsLoading(true);
        await waitForSdkReady();
        syncSdkClientContext();

        try {
            const accessToken = getCookie("accessToken");

            if (!accessToken) {
                const refreshed = await refreshAccessToken();
                if (!refreshed) {
                    await attemptCentralSessionAutoLogin();
                }
            } else {
                await axios.get(`${OAUTH}/user-token-info`);
            }
        } catch (error) {
            const recovered = await attemptCentralSessionAutoLogin();
            if (!recovered) {
                doLogout();
            }
        } finally {
            setIsLoading(false);
        }
    }, [attemptCentralSessionAutoLogin, doLogout, refreshAccessToken, syncSdkClientContext, waitForSdkReady]);

    useEffect(() => {
        initializeAuth();
    }, [initializeAuth]);

    const contextValue = {
        token,
        saveToken,
        refreshToken,
        saveRefreshToken,
        isAuthenticated,
        saveAuthenticated,
        tokenExpiresIn,
        saveTokenExpiresIn,
        role,
        saveRole,
        user,
        saveUser,
        doLogout,
    };

    if (isLoading) {
        return <div></div>;
    }

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
